/** @jsx jsx */
import React, { useCallback, useEffect, type SyntheticEvent, useState } from 'react';
import { jsx, css } from '@compiled/react';
import { token } from '@atlaskit/tokens';

import { type UIAnalyticsEvent, useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import { IconButton } from '@atlaskit/button/new';
import { Label } from '@atlaskit/form';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	type KeyboardOrMouseEvent,
} from '@atlaskit/modal-dialog';
import { Text, Flex, xcss, Grid } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import Textfield from '@atlaskit/textfield';
import { MountEvent } from '@atlassian/analytics-bridge';
import { AnalyticsSource } from '@atlassian/jira-analytics-web-react/src/components/decorators.tsx';
import ViewTracker from '@atlassian/jira-analytics-web-react/src/components/view-tracker.tsx';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import ErrorBoundary, {
	type ExtraErrorAttributes,
} from '@atlassian/jira-error-boundary/src/ErrorBoundary.tsx';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useFlagService } from '@atlassian/jira-flags'; // ignore-for-ENGHEALTH-17759
import { useIntl } from '@atlassian/jira-intl';
import { useArchiveIssueUrl } from '@atlassian/jira-issue-archival-archives/src/controllers/useArchiveIssueUrl.tsx';
import { useArchiveIssue } from '@atlassian/jira-issue-archival-modal/src/services/index.tsx';
import { isIssueViewPage } from '@atlassian/jira-issue-archival-modal/src/utils.tsx';
import { sendExperienceAnalytics } from '@atlassian/jira-issue-view-analytics/src/controllers/send-experience-analytics/index.tsx';
import { usePrevious } from '@atlassian/jira-platform-react-hooks-use-previous/src/common/utils/index.tsx';
import { MODAL, fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import {
	useProjectKey,
	useApplication,
	useEdition,
} from '@atlassian/jira-project-context-service/src/main.tsx';
import type { Application } from '@atlassian/jira-shared-types/src/application.tsx';
import type { ApplicationEdition } from '@atlassian/jira-shared-types/src/edition.tsx';
import type { IssueId } from '@atlassian/jira-shared-types/src/general.tsx';
import { ModalDialog } from '@atlassian/jira-software-modal-dialog/src/ui/modal-dialog/index.tsx';
import messages from './messages.tsx';
import type { IssueDeleteCallbackArgs } from './types.tsx';
import { useIsDeleteIssueExperimentEnabled } from './useIsDeleteIssueExperimentEnabled.tsx';

export type DeleteIssueViewProps = {
	issueId: IssueId;
	isDeleteIssueModalOpen: boolean;
	isDeletingIssue: boolean;
	issueKey: string;
	isSimplifiedProject: boolean;
	shouldShowChildSpecificCopy: boolean;
	didIssueDeleteFail: boolean;
	// eslint-disable-next-line jira/react/handler-naming
	triggerDeleteRequest: (
		issueKey: string,
		analytics: UIAnalyticsEvent,
		application: Application | undefined,
		edition: ApplicationEdition | undefined,
	) => void;
	// eslint-disable-next-line jira/react/handler-naming
	triggerCloseDeleteIssueModal: () => void;
	// eslint-disable-next-line jira/react/handler-naming
	deleteCancelAnalytics: (arg1: UIAnalyticsEvent) => void;
	// eslint-disable-next-line jira/react/handler-naming
	deleteConfirmAnalytics: (arg1: UIAnalyticsEvent) => void;
	onIssueDeleteSuccess: ((arg1: IssueDeleteCallbackArgs) => void) | undefined;
	onIssueDeleteFailure: ((arg1: IssueDeleteCallbackArgs) => void) | undefined;
	onClose: () => void;
};

const DeleteIssueViewWithErrorBoundaryOld = AnalyticsSource(
	'issueDelete',
	MODAL,
)(({
	issueId,
	isDeleteIssueModalOpen,
	isDeletingIssue,
	issueKey,
	isSimplifiedProject,
	shouldShowChildSpecificCopy,
	didIssueDeleteFail,
	triggerDeleteRequest,
	triggerCloseDeleteIssueModal,
	deleteCancelAnalytics,
	deleteConfirmAnalytics,
	onIssueDeleteSuccess,
	onIssueDeleteFailure,
	onClose,
}: DeleteIssueViewProps) => {
	if (!isDeleteIssueModalOpen) return null;

	const { formatMessage } = useIntl();
	const previousIsDeletingIssue = usePrevious(isDeletingIssue);
	const projectKey = useProjectKey(issueKey);
	const application = useApplication(projectKey, true);
	const edition = useEdition(projectKey, true);

	useEffect(() => {
		const didDeletionFinish = !isDeletingIssue && previousIsDeletingIssue;

		if (didDeletionFinish) {
			if (!didIssueDeleteFail) {
				triggerCloseDeleteIssueModal();
				onIssueDeleteSuccess?.({
					issueId,
					issueKey,
				});
				onClose();
			} else {
				onIssueDeleteFailure?.({
					issueId,
					issueKey,
				});
			}
		}
	}, [
		didIssueDeleteFail,
		isDeletingIssue,
		issueId,
		issueKey,
		onClose,
		onIssueDeleteFailure,
		onIssueDeleteSuccess,
		previousIsDeletingIssue,
		triggerCloseDeleteIssueModal,
	]);

	const onConfirmDeleteIssue = useCallback(
		(event: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
			deleteConfirmAnalytics(analyticsEvent);
			triggerDeleteRequest(issueKey, analyticsEvent, application, edition);
		},
		[application, deleteConfirmAnalytics, edition, issueKey, triggerDeleteRequest],
	);

	const onCancel = (event: KeyboardOrMouseEvent, analyticsEvent: UIAnalyticsEvent) => {
		deleteCancelAnalytics(analyticsEvent);
		triggerCloseDeleteIssueModal();
	};

	const getChildConditionalDeleteHeading = () => {
		const deleteWithChildrenHeadingMessage = fg('jira-issue-terminology-refresh-m3')
			? messages.deleteWithChildrenHeadingIssueTermRefresh
			: messages.deleteWithChildrenHeading;

		if (shouldShowChildSpecificCopy) {
			return isSimplifiedProject
				? deleteWithChildrenHeadingMessage
				: messages.deleteWithSubtasksHeading;
		}
		return messages.deleteHeading;
	};

	const getChildConditionalDeleteWarning = () => {
		const deleteIssueWarning = fg('issue_delete_two_step_process')
			? messages.deleteOrArchiveWarning
			: messages.deleteWarning;
		const deleteWithChildrenWarningMessage = fg('jira-issue-terminology-refresh-m3')
			? messages.deleteWithChildrenWarningIssueTermRefresh
			: messages.deleteWithChildrenWarning;

		const deleteWithSubtasksWarningMessage = fg('jira-issue-terminology-refresh-m3')
			? messages.deleteWithSubtasksWarningIssueTermRefresh
			: messages.deleteWithSubtasksWarning;

		if (shouldShowChildSpecificCopy) {
			return isSimplifiedProject
				? deleteWithChildrenWarningMessage
				: deleteWithSubtasksWarningMessage;
		}
		return fg('jira-issue-terminology-refresh-m3')
			? messages.deleteWarningIssueTermRefresh
			: deleteIssueWarning;
	};

	const cancelButton = (
		<Button
			appearance="subtle"
			onClick={onCancel}
			/* eslint-disable-next-line jira/integration/test-id-by-folder-structure */
			testId="issue.views.issue-base.foundation.issue-actions.delete-issue.cancel-button"
			// @ts-expect-error - TS2322 - Type '{ children: string; appearance: "subtle"; onClick: (event: SyntheticEvent<Element, Event>, analyticsEvent: UIAnalyticsEventInterface) => void; testId: string; disabled: boolean; }' is not assignable to type 'IntrinsicAttributes & ButtonProps & RefAttributes<HTMLElement>'.
			disabled={isDeletingIssue}
			key="cancelButton"
			interactionName="auto-generated.issue.views.issue-base.foundation.issue-actions.delete-issue.cancel-button.button"
		>
			{formatMessage(messages.cancel)}
		</Button>
	);
	const deleteButton = (
		<Button
			appearance="danger"
			onClick={onConfirmDeleteIssue}
			/* eslint-disable-next-line jira/integration/test-id-by-folder-structure */
			testId="issue.views.issue-base.foundation.issue-actions.delete-issue.confirm-button"
			disabled={isDeletingIssue}
			// @ts-expect-error - TS2322 - Type 'Element | null' is not assignable to type 'ReactChild | undefined'.
			iconAfter={isDeletingIssue ? <Spinner size="small" /> : null}
			key="deleteButton"
			interactionName="auto-generated.issue.views.issue-base.foundation.issue-actions.delete-issue.confirm-button.button1"
		>
			{formatMessage(messages.delete)}
		</Button>
	);

	return (
		<>
			<ViewTracker />
			<ShortcutScope>
				<ModalDialog width="small" onClose={onCancel}>
					<ModalHeader>
						<ModalTitle appearance="danger">
							{formatMessage(getChildConditionalDeleteHeading(), {
								issueKey,
							})}
						</ModalTitle>
					</ModalHeader>
					<ModalBody>
						<Text as="p">{formatMessage(getChildConditionalDeleteWarning())}</Text>
						<Text as="p">
							{formatMessage(
								fg('jira-issue-terminology-refresh-m3')
									? messages.deleteRecommendationIssueTermRefresh
									: messages.deleteRecommendation,
							)}
						</Text>
					</ModalBody>
					<ModalFooter>
						{cancelButton}
						{deleteButton}
					</ModalFooter>
				</ModalDialog>
			</ShortcutScope>
			<MountEvent
				onMount={() => {
					sendExperienceAnalytics({
						experience: 'issueViewDeleteIssueModalRender',
						wasExperienceSuccesful: true,
						analyticsSource: 'issueViewIssueActionsDeleteIssue',
						application: application ?? null,
						edition: edition ?? null,
					});
				}}
			/>
		</>
	);
});

const DeleteIssueViewWithErrorBoundaryNew = AnalyticsSource(
	'issueDelete',
	MODAL,
)(({
	issueId,
	isDeleteIssueModalOpen,
	isDeletingIssue,
	issueKey,
	isSimplifiedProject,
	shouldShowChildSpecificCopy,
	didIssueDeleteFail,
	triggerDeleteRequest,
	triggerCloseDeleteIssueModal,
	deleteCancelAnalytics,
	deleteConfirmAnalytics,
	onIssueDeleteSuccess,
	onIssueDeleteFailure,
	onClose,
}: DeleteIssueViewProps) => {
	if (!isDeleteIssueModalOpen) return null;

	const { formatMessage } = useIntl();
	const previousIsDeletingIssue = usePrevious(isDeletingIssue);
	const projectKey = useProjectKey(issueKey);
	const application = useApplication(projectKey, true);
	const edition = useEdition(projectKey, true);

	const [inputValue, setInputValue] = useState('');
	const archiveIssue = useArchiveIssue();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isEnabled = useIsDeleteIssueExperimentEnabled();
	const { showFlag } = useFlagService();
	const archiveIssueUrl = useArchiveIssueUrl();

	useEffect(() => {
		const didDeletionFinish = !isDeletingIssue && previousIsDeletingIssue;

		if (didDeletionFinish) {
			if (!didIssueDeleteFail) {
				triggerCloseDeleteIssueModal();
				onIssueDeleteSuccess?.({
					issueId,
					issueKey,
				});
				onClose();
			} else {
				onIssueDeleteFailure?.({
					issueId,
					issueKey,
				});
			}
		}
	}, [
		didIssueDeleteFail,
		isDeletingIssue,
		issueId,
		issueKey,
		onClose,
		onIssueDeleteFailure,
		onIssueDeleteSuccess,
		previousIsDeletingIssue,
		triggerCloseDeleteIssueModal,
	]);

	const onConfirmDeleteIssue = useCallback(
		(event: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
			deleteConfirmAnalytics(analyticsEvent);
			triggerDeleteRequest(issueKey, analyticsEvent, application, edition);
		},
		[application, deleteConfirmAnalytics, edition, issueKey, triggerDeleteRequest],
	);

	const onCancel = () => {
		deleteCancelAnalytics(
			createAnalyticsEvent({
				action: 'deleteIssueCancel',
				actionSubject: 'issue',
			}),
		);
		triggerCloseDeleteIssueModal();
	};

	const onArchivalFailure = () => {
		showFlag({
			type: 'error',
			title: formatMessage(messages.issueArchivalErrorFlagTitle, {
				issueKey,
			}),
			testId: 'jira-issue-archive.modal.error-flag',
		});
		fireTrackAnalytics(
			createAnalyticsEvent({
				action: 'archived',
				actionSubject: 'issue',
				attributes: { triggerPointKey: 'DeleteIssueModal' },
			}),
			'archiveIssue Failed',
		);
		triggerCloseDeleteIssueModal();
	};

	const onArchivalSuccess = () => {
		fireTrackAnalytics(
			createAnalyticsEvent({
				action: 'archived',
				actionSubject: 'issue',
				attributes: { triggerPointKey: 'DeleteIssueModal' },
			}),
			'archiveIssue Successful',
		);

		if (isIssueViewPage()) {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.location.reload();
		}

		showFlag({
			type: 'success',
			title: formatMessage(messages.issueArchivalSuccessFlagTitle, {
				issueKey,
			}),
			testId: 'jira-issue-archive.modal.success-flag',
			actions: [
				{
					content: messages.issueArchivalSuccessFlagActionViewIssue,
					href: `/browse/${issueKey}`,
				},
				{
					content: messages.issueArchivalSuccessFlagActionViewInArchive,
					href: archiveIssueUrl,
				},
			],
		});
		onIssueDeleteSuccess?.({
			issueId,
			issueKey,
		});
	};

	const onArchive = () => {
		archiveIssue(issueKey, onArchivalSuccess, onArchivalFailure);
		onClose?.();
		triggerCloseDeleteIssueModal();
	};

	const getChildConditionalDeleteWarning = () => {
		const deleteWithChildrenWarningMessage = messages.deleteOrArchiveWithChildrenWarning;

		const deleteWithSubtasksWarningMessage = messages.deleteOrArchiveWithSubtasksWarning;

		if (shouldShowChildSpecificCopy) {
			return isSimplifiedProject
				? deleteWithChildrenWarningMessage
				: deleteWithSubtasksWarningMessage;
		}
		return messages.deleteOrArchiveWarning;
	};

	const archiveButton = (
		<Button
			appearance="subtle"
			onClick={onArchive}
			/* eslint-disable-next-line jira/integration/test-id-by-folder-structure */
			testId="issue.views.issue-base.foundation.issue-actions.delete-issue.archive-button"
			// @ts-expect-error - TS2322 - Type '{ children: string; appearance: "subtle"; onClick: (event: SyntheticEvent<Element, Event>, analyticsEvent: UIAnalyticsEventInterface) => void; testId: string; disabled: boolean; }' is not assignable to type 'IntrinsicAttributes & ButtonProps & RefAttributes<HTMLElement>'.
			disabled={isDeletingIssue}
			key="archiveButton"
			interactionName="auto-generated.issue.views.issue-base.foundation.issue-actions.delete-issue.archive-button.button"
		>
			{formatMessage(messages.archive)}
		</Button>
	);

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setInputValue(value);
	};

	const deleteButton = (
		<Button
			appearance={
				inputValue.trim().toLowerCase() !==
				formatMessage(messages.deleteTextToEquateForTextbox).toLowerCase()
					? 'primary'
					: 'danger'
			}
			isDisabled={
				inputValue.trim().toLowerCase() !==
					formatMessage(messages.deleteTextToEquateForTextbox).toLowerCase() || isDeletingIssue
			}
			onClick={onConfirmDeleteIssue}
			/* eslint-disable-next-line jira/integration/test-id-by-folder-structure */
			testId="issue.views.issue-base.foundation.issue-actions.delete-issue.confirm-button"
			disabled={isDeletingIssue}
			// @ts-expect-error - TS2322 - Type 'Element | null' is not assignable to type 'ReactChild | undefined'.
			iconAfter={isDeletingIssue ? <Spinner size="small" /> : null}
			key="deleteButton"
			interactionName="auto-generated.issue.views.issue-base.foundation.issue-actions.delete-issue.confirm-button.button1"
		>
			{formatMessage(messages.delete)}
		</Button>
	);

	if (!isEnabled) {
		return (
			<DeleteIssueViewWithErrorBoundaryOld
				issueId={issueId}
				isDeleteIssueModalOpen={isDeleteIssueModalOpen}
				isDeletingIssue={isDeletingIssue}
				issueKey={issueKey}
				isSimplifiedProject={isSimplifiedProject}
				shouldShowChildSpecificCopy={shouldShowChildSpecificCopy}
				didIssueDeleteFail={didIssueDeleteFail}
				triggerDeleteRequest={triggerDeleteRequest}
				triggerCloseDeleteIssueModal={triggerCloseDeleteIssueModal}
				deleteCancelAnalytics={deleteCancelAnalytics}
				deleteConfirmAnalytics={deleteConfirmAnalytics}
				onIssueDeleteSuccess={onIssueDeleteSuccess}
				onIssueDeleteFailure={onIssueDeleteFailure}
				onClose={onClose}
			/>
		);
	}
	return (
		<>
			<ViewTracker />
			<ShortcutScope>
				<ModalDialog width="small" onClose={onCancel}>
					<ModalHeader>
						<Grid gap="space.250" templateAreas={['title close']} xcss={gridStyles}>
							<Flex xcss={titleContainerStyles} justifyContent="start">
								<ModalTitle appearance="danger">
									{formatMessage(messages.deleteOrArchiveHeading, {
										issueKey,
									})}
								</ModalTitle>
							</Flex>
							<Flex xcss={closeContainerStyles} justifyContent="end">
								<IconButton
									appearance="subtle"
									icon={CrossIcon}
									label={formatMessage(messages.iconButtonLabel)}
									onClick={onCancel}
								/>
							</Flex>
						</Grid>
					</ModalHeader>
					<ModalBody>
						<Text as="p">{formatMessage(getChildConditionalDeleteWarning())}</Text>
						<Label htmlFor="Textfield">
							<br />
							<span css={SpanStyle}>
								{formatMessage(messages.deleteTextFieldLabelMessage, {
									// eslint-disable-next-line @typescript-eslint/no-explicit-any
									strong: (...chunks: any[]) => (
										<Text size="small" as="strong">
											{chunks.join('')}
										</Text>
									),
								})}
							</span>
						</Label>
						<Textfield
							label={formatMessage(messages.deleteTextFieldLabelMessage)}
							value={inputValue}
							onChange={handleInputChange}
							width="352"
							height="36"
						/>
					</ModalBody>

					<ModalFooter>
						{archiveButton}
						{deleteButton}
					</ModalFooter>
				</ModalDialog>
			</ShortcutScope>
			<MountEvent
				onMount={() => {
					sendExperienceAnalytics({
						experience: 'issueViewDeleteIssueModalRender',
						wasExperienceSuccesful: true,
						analyticsSource: 'issueViewIssueActionsDeleteIssue',
						application: application ?? null,
						edition: edition ?? null,
					});
				}}
			/>
		</>
	);
});

export const DeleteIssueViewWithErrorBoundary = componentWithFG(
	'issue_delete_two_step_process',
	DeleteIssueViewWithErrorBoundaryNew,
	DeleteIssueViewWithErrorBoundaryOld,
);

export const DeleteIssueView = AnalyticsSource(
	'issueDelete',
	MODAL,
)((props: DeleteIssueViewProps) => {
	const { issueKey } = props;
	const projectKey = useProjectKey(issueKey);
	const application = useApplication(projectKey, true);
	const edition = useEdition(projectKey, true);

	return (
		<ErrorBoundary
			id="issue.issue-view-foundation.issue-actions.delete-issue"
			onError={(location: string, error: Error, attributes: ExtraErrorAttributes) => {
				const { componentStack } = attributes;

				sendExperienceAnalytics({
					experience: 'issueViewDeleteIssueModalRender',
					wasExperienceSuccesful: false,
					analyticsSource: 'issueViewIssueActionsDeleteIssue',
					application: application ?? null,
					edition: edition ?? null,
					...(fg('thor_populate_missing_attributes_across_issue_view') && {
						additionalAttributes: {
							errorMessage: error?.message || 'UNKNOWN_ERROR',
							location,
							...(componentStack && { componentStack }),
						},
					}),
				});
			}}
		>
			<DeleteIssueViewWithErrorBoundary {...props} />
		</ErrorBoundary>
	);
});

export default DeleteIssueView;

const SpanStyle = css({
	fontWeight: token('font.weight.regular'),
});

const closeContainerStyles = xcss({
	gridArea: 'close',
});

const titleContainerStyles = xcss({
	gridArea: 'title',
});

const gridStyles = xcss({
	width: '100%',
});
